import Box from '@mui/material/Box';
import { useIntl } from '@doxyme/locale';
import { modalClasses } from '../modal.classes';
import { type ModalProps } from '../modal';
import { useModalHeaderStyles } from './modalHeader.styles';
import { IconButton } from '@doxyme/ui-foundation/components/iconButton';
import { Icon } from '@doxyme/icons';
import { BodyM, Heading } from '@doxyme/ui-foundation/typography';
import { useTheme } from '@doxyme/ui-foundation/theme';
import {
  InfoIcon,
  InfoIconProps,
} from '@doxyme/ui-foundation/components/infoIcon';

import type { JSX } from 'react';
import { useViewport } from '@doxyme/ui-foundation/hooks';

export type ModalHeaderProps = Pick<ModalProps, 'styles'> & {
  title?: string | JSX.Element;
  titleId?: string;
  subtitle?: string;
  icon?: JSX.Element;
  infoIcon?: InfoIconProps;
  hasMultiLineSubtitle?: boolean;
  onClose?: () => void;
};

/**
 * Renders the title and subtitle for the Modal at the top. Has to be placed within `<ModalContent>`
 */
export const ModalHeader = ({
  title,
  titleId,
  styles,
  subtitle,
  icon,
  infoIcon,
  hasMultiLineSubtitle = false,
  onClose,
}: ModalHeaderProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const css = useModalHeaderStyles({
    isEmpty: !Boolean(title) && !Boolean(subtitle) && !Boolean(icon),
    hasMultiLineSubtitle,
  });
  const theme = useTheme();
  const { isSmallViewport } = useViewport();

  return (
    <Box
      css={css.container}
      className={modalClasses.header}
      style={styles?.header}
    >
      <Box css={css.innerContainer}>
        {Boolean(icon) && <Box css={css.iconContainer}>{icon}</Box>}

        <Box css={css.titleContainer}>
          {Boolean(title) && (
            <Heading
              tag='h2'
              variant='titleH4'
              id={titleId}
              className={modalClasses.title}
              fontWeight='medium'
              css={{
                ...css.title,
                ...styles?.title,
              }}
            >
              {title}
            </Heading>
          )}

          {Boolean(subtitle) && (
            <Box css={css.subtitle}>
              <BodyM color={theme.text.generic.tertiary}>{subtitle}</BodyM>
              {Boolean(infoIcon) && <InfoIcon {...infoIcon} />}
            </Box>
          )}
        </Box>

        {Boolean(onClose) && (
          <IconButton
            type='subtle'
            size='medium'
            title={formatMessage({ id: 'close' })}
            className={modalClasses.closeButton}
            onClick={(): void => onClose?.()}
            css={{ ...styles?.closeButton }}
            tooltipPlacement={isSmallViewport ? 'left' : undefined}
            icon={
              <Icon.Close
                color={theme.icon.primary}
                className={modalClasses.closeIcon}
                style={styles?.closeIcon}
              />
            }
          />
        )}
      </Box>
    </Box>
  );
};
